import React, { useEffect } from 'react';
import { ArrowLeft, Cookie } from 'lucide-react';

interface PoliticaCookiesProps {
  navigateToPage: (page: string) => void;
}

export default function PoliticaCookies({ navigateToPage }: PoliticaCookiesProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
        {/* Cabecera / Banner superior */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-8 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10 pointer-events-none">
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white"></div>
            <div className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full bg-white"></div>
          </div>
          
          <button
            onClick={() => navigateToPage('home')}
            className="inline-flex items-center text-blue-100 hover:text-white mb-6 transition-colors group focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-700 rounded-lg p-1"
            aria-label="Volver a la página de inicio"
          >
            <ArrowLeft className="h-5 w-5 mr-2 group-hover:-translate-x-1 transition-transform" />
            Volver al inicio
          </button>
          
          <div className="flex items-center gap-4">
            <div className="bg-white/10 p-3 rounded-xl backdrop-blur-sm">
              <Cookie className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Política de Cookies</h1>
              <p className="text-blue-100 text-sm mt-1">Información transparente sobre el uso de cookies</p>
            </div>
          </div>
        </div>

        {/* Contenido Legal */}
        <div className="p-8 sm:p-10 space-y-8 text-gray-700 leading-relaxed">
          <section className="border-b border-gray-100 pb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">1. ¿Qué son las Cookies?</h2>
            <p>
              Una cookie es un pequeño archivo de texto que se almacena en su navegador (computadora, tableta o dispositivo móvil) cuando visita un sitio web. Las cookies permiten que una página web, entre otras cosas, recuerde sus preferencias, recopile estadísticas de uso, le ofrezca una experiencia de navegación personalizada y garantice el correcto funcionamiento técnico de la web.
            </p>
          </section>

          <section className="border-b border-gray-100 pb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">2. Tipos de Cookies que Utiliza este Sitio Web</h2>
            <p className="mb-4">
              Este sitio web utiliza cookies técnicas y funcionales que son esenciales para su correcto funcionamiento, y cookies de personalización para recordar sus decisiones (como el consentimiento de cookies). A continuación, detallamos las categorías de cookies que podemos emplear:
            </p>
            
            <div className="space-y-4">
              <div className="bg-blue-50/50 p-4 rounded-xl border border-blue-100">
                <h3 className="font-bold text-gray-900 mb-1">A. Cookies Técnicas (Estrictamente Necesarias)</h3>
                <p className="text-sm">
                  Son aquellas indispensables para la navegación y el buen funcionamiento de la web. Permiten funciones básicas como la navegación de páginas, la seguridad del sitio web o el acceso a zonas privadas (como el panel de administración). Sin estas cookies, la página web no puede funcionar adecuadamente. No requieren consentimiento legal previo.
                </p>
              </div>

              <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                <h3 className="font-bold text-gray-900 mb-1">B. Cookies de Personalización o Preferencias</h3>
                <p className="text-sm">
                  Permiten que el sitio web recuerde información que cambia la forma en que se comporta o se ve la página, como su idioma preferido o la región en la que se encuentra. En nuestro caso, se utilizan para recordar si ha aceptado o rechazado la Política de Cookies.
                </p>
              </div>
            </div>
          </section>

          <section className="border-b border-gray-100 pb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">3. Tabla Detallada de las Cookies Utilizadas</h2>
            <p className="mb-4">
              A continuación, se describen las cookies específicas que se almacenan en el dispositivo del usuario al navegar por nuestro sitio web:
            </p>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm text-left border border-gray-200 rounded-lg overflow-hidden">
                <thead className="bg-gray-50 font-bold text-gray-900">
                  <tr>
                    <th className="px-4 py-3 border-b border-r">Nombre</th>
                    <th className="px-4 py-3 border-b border-r">Tipo</th>
                    <th className="px-4 py-3 border-b border-r">Finalidad</th>
                    <th className="px-4 py-3 border-b">Duración</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 border-r font-mono text-xs">cookieConsent</td>
                    <td className="px-4 py-3 border-r">Propia / Necesaria</td>
                    <td className="px-4 py-3 border-r">Almacena el estado de consentimiento del usuario sobre el uso de cookies en el sitio.</td>
                    <td className="px-4 py-3">1 año</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 border-r font-mono text-xs">cookiePreferences</td>
                    <td className="px-4 py-3 border-r">Propia / Necesaria</td>
                    <td className="px-4 py-3 border-r">Almacena las preferencias detalladas del usuario sobre qué tipos de cookies acepta (técnicas, analíticas, marketing).</td>
                    <td className="px-4 py-3">1 año</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 border-r font-mono text-xs">session (Admin panel)</td>
                    <td className="px-4 py-3 border-r">Propia / Técnica</td>
                    <td className="px-4 py-3 border-r">Mantiene la sesión de usuario autenticado activa en el panel de administración (/panel-control-jl).</td>
                    <td className="px-4 py-3">Sesión</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="mt-4 text-sm text-gray-500">
              * Nota: Este sitio web no utiliza de forma activa cookies de análisis de terceros (como Google Analytics) ni de marketing/publicidad a menos que sean explícitamente autorizadas y configuradas por el usuario. Tampoco integramos redes sociales que realicen un seguimiento invasivo sin su conocimiento.
            </p>
          </section>

          <section className="border-b border-gray-100 pb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">4. ¿Cómo Puede Administrar, Desactivar o Bloquear las Cookies?</h2>
            <p className="mb-2">
              Usted tiene la posibilidad de revocar su consentimiento en cualquier momento o cambiar sus preferencias a través de los mecanismos de configuración que ofrece nuestro sitio web. Además, puede permitir, bloquear o eliminar las cookies instaladas en su equipo mediante la configuración de las opciones del navegador instalado en su ordenador o dispositivo móvil:
            </p>
            <ul className="list-disc pl-5 mt-2 space-y-2">
              <li>
                <strong>Google Chrome:</strong> <a href="https://support.google.com/chrome/answer/95647?hl=es" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Instrucciones para Chrome</a>
              </li>
              <li>
                <strong>Mozilla Firefox:</strong> <a href="https://support.mozilla.org/es/kb/habilitar-y-deshabilitar-cookies-sitios-web-rastrear-preferencias" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Instrucciones para Firefox</a>
              </li>
              <li>
                <strong>Apple Safari:</strong> <a href="https://support.apple.com/es-es/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Instrucciones para Safari</a>
              </li>
              <li>
                <strong>Microsoft Edge:</strong> <a href="https://support.microsoft.com/es-es/microsoft-edge/eliminar-y-administrar-cookies-168dab11-0753-043d-7c16-ede5947fc64d" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Instrucciones para Edge</a>
              </li>
            </ul>
            <p className="mt-3">
              Tenga en cuenta que el bloqueo o la desactivación de todas las cookies puede afectar parcial o totalmente a la correcta visualización de algunas áreas del sitio web o impedir el correcto funcionamiento de ciertos servicios interactivos.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">5. Más Información</h2>
            <p>
              Para más detalles sobre el tratamiento de sus datos personales, la seguridad de la información y sus derechos legales, le invitamos a consultar nuestra{' '}
              <button
                onClick={() => navigateToPage('privacidad')}
                className="text-blue-600 hover:text-blue-800 font-semibold underline focus:outline-none"
              >
                Política de Privacidad
              </button>.
            </p>
          </section>
        </div>

        {/* Footer interior de la página */}
        <div className="bg-gray-50 px-8 py-6 border-t border-gray-100 flex justify-between items-center flex-wrap gap-4 text-sm text-gray-500">
          <span>Última actualización: Julio de 2026</span>
          <button
            onClick={() => navigateToPage('home')}
            className="text-blue-600 hover:text-blue-800 font-semibold underline focus:outline-none"
          >
            Volver al Inicio
          </button>
        </div>
      </div>
    </div>
  );
}
